import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Store, 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Building2, 
  Camera, 
  Sparkles, 
  Flower2, 
  Music, 
  UtensilsCrossed,
  Image,
  Link as LinkIcon,
  CheckCircle2,
  PartyPopper
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'venues', name: 'Venues', icon: Building2 },
  { id: 'photographers', name: 'Photographers', icon: Camera },
  { id: 'makeup', name: 'Bridal Makeup', icon: Sparkles },
  { id: 'mehndi', name: 'Mehndi Artists', icon: Sparkles },
  { id: 'decorators', name: 'Decorators', icon: Flower2 },
  { id: 'dj', name: 'DJs & Music', icon: Music },
  { id: 'catering', name: 'Caterers', icon: UtensilsCrossed },
  { id: 'bridal-wear', name: 'Bridal Wear', icon: Sparkles },
  { id: 'invitations', name: 'Invitations', icon: Image },
  { id: 'entertainment', name: 'Entertainment & Arts', icon: PartyPopper },
];

const BecomeVendorPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    axios.get(`${API}/vendors/categories`).then(res => {
      setCities(res.data.cities || []);
    }).catch(() => { toast.error('Failed to load cities'); });
  }, []);

  const [formData, setFormData] = useState({
    business_name: '',
    category: '',
    description: '',
    services: '',
    price_range_min: '',
    price_range_max: '',
    cities: [],
    address: '',
    phone: '',
    contact_email: '',
    website: '',
    social_media: '',
    images: '',
    special_promotion: false,
  });

  const handleNext = () => {
    if (step === 1) {
      if (!formData.business_name || !formData.category) {
        toast.error('Please fill in required fields');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!formData.contact_email || !formData.phone) {
      toast.error('Email and phone number are required');
      return;
    }
    setLoading(true);
    try {
      // Convert empty strings to null for optional fields
      const emptyToNull = (val) => (val && val.trim() !== '' ? val.trim() : null);
      
      const data = {
        business_name: formData.business_name,
        category: formData.category,
        description: formData.description,
        services: formData.services.split(',').map(s => s.trim()).filter(Boolean),
        price_range_min: parseInt(formData.price_range_min) || 0,
        price_range_max: parseInt(formData.price_range_max) || 0,
        city: formData.cities[0] || 'Toronto',
        cities: formData.cities,
        address: emptyToNull(formData.address),
        phone: emptyToNull(formData.phone),
        contact_email: emptyToNull(formData.contact_email),
        website: emptyToNull(formData.website),
        social_media: emptyToNull(formData.social_media),
        images: formData.images.split(',').map(s => s.trim()).filter(Boolean),
        special_promotion: formData.special_promotion,
      };

      const response = await axios.post(`${API}/vendor-applications`, data);
      setApplicationId(response.data.id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-soft border border-gray-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
              Application Submitted!
            </h1>
            <p className="text-gray-500 mb-6">
              Thank you for registering! You can add photos to your Plan My Shaadi vendor page by emailing them to <a href="mailto:admin@planmyshaadi.ca" className="text-maroon-700 font-medium underline">admin@planmyshaadi.ca</a>.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              Application ID: <span className="font-mono">{applicationId}</span>
            </p>
            <Link to="/">
              <Button className="btn-primary">
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Store className="w-7 h-7 text-teal-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            Become a Vendor
          </h1>
          <p className="text-gray-500">
            Join Montreal's premier South Asian wedding marketplace
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                s < step ? 'bg-teal-500 text-white' :
                s === step ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 rounded ${s < step ? 'bg-teal-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 md:p-8 shadow-soft border border-gray-100"
        >
          {/* Step 1: Business Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-xl font-semibold mb-1">Business Information</h2>
                <p className="text-sm text-gray-500">Tell us about your business</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Business Name *</label>
                <Input
                  placeholder="Your business name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  className="h-12 rounded-xl"
                  data-testid="vendor-business-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Category *</label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="vendor-category">
                    <SelectValue placeholder="Select your category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl z-[9999]" position="popper" sideOffset={4}>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} data-testid={`category-option-${cat.id}`}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Description</label>
                <Textarea
                  placeholder="Tell couples about your services..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-xl min-h-[120px]"
                  data-testid="vendor-description"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Services (comma separated)</label>
                <Input
                  placeholder="e.g., Bridal Makeup, Hair Styling, Engagement Looks"
                  value={formData.services}
                  onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Starting Price ($)</label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={formData.price_range_min}
                    onChange={(e) => setFormData({ ...formData, price_range_min: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Max Price ($)</label>
                  <Input
                    type="number"
                    placeholder="5000"
                    value={formData.price_range_max}
                    onChange={(e) => setFormData({ ...formData, price_range_max: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Locations Served (select all that apply)</label>
                <div className="border rounded-xl p-3 max-h-48 overflow-y-auto space-y-1" data-testid="vendor-city-select">
                  {cities.map((city) => (
                    <label key={city} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.cities.includes(city)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, cities: [...formData.cities, city] });
                          } else {
                            setFormData({ ...formData, cities: formData.cities.filter(c => c !== city) });
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-maroon-600 focus:ring-maroon-500"
                      />
                      <span className="text-sm">{city}</span>
                    </label>
                  ))}
                </div>
                {formData.cities.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{formData.cities.length} location(s) selected</p>
                )}
              </div>

              {/* Special Promotion */}
              <div className="bg-gradient-to-r from-gold-50 to-amber-50 border border-gold-200 rounded-xl p-4" data-testid="special-promotion-section">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.special_promotion}
                    onChange={(e) => setFormData({ ...formData, special_promotion: e.target.checked })}
                    className="w-5 h-5 rounded border-gold-300 text-gold-600 focus:ring-gold-500 mt-0.5"
                    data-testid="special-promotion-checkbox"
                  />
                  <div>
                    <span className="font-semibold text-maroon-900">Special Promotion – $99 for 3 months</span>
                    <p className="text-sm text-gray-600 mt-0.5">Stay at the top in your category.</p>
                    <p className="text-xs text-gray-400 mt-1 italic">Our team will reach out with further details after selection.</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 2: Photos & Links */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-xl font-semibold mb-1">Photos & Links</h2>
                <p className="text-sm text-gray-500">Add images and your online presence</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Image URLs (comma separated)
                </label>
                <Textarea
                  placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
                  value={formData.images}
                  onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                  className="rounded-xl"
                  rows={3}
                />
                <p className="text-xs text-gray-400 mt-1">Add links to your best work photos</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Website
                </label>
                <Input
                  placeholder="https://yourbusiness.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Social Media</label>
                <Input
                  placeholder="@yourbusiness or full URL"
                  value={formData.social_media}
                  onChange={(e) => setFormData({ ...formData, social_media: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Address (Optional)</label>
                <Input
                  placeholder="123 Main St, Montreal, QC"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          {/* Step 3: Contact & Review */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-xl font-semibold mb-1">Contact & Review</h2>
                <p className="text-sm text-gray-500">Add contact info and review your application</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Contact Email *</label>
                <Input
                  type="email"
                  placeholder="contact@yourbusiness.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="h-12 rounded-xl"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Phone Number *</label>
                <Input
                  placeholder="(514) 555-0100"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 rounded-xl"
                  required
                />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="font-medium text-sm text-gray-700">Application Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-500">Business:</div>
                  <div className="font-medium">{formData.business_name || '-'}</div>
                  <div className="text-gray-500">Category:</div>
                  <div className="font-medium capitalize">{formData.category?.replace('-', ' ') || '-'}</div>
                  <div className="text-gray-500">Locations:</div>
                  <div className="font-medium">{formData.cities.length > 0 ? formData.cities.join(', ') : '-'}</div>
                  <div className="text-gray-500">Price Range:</div>
                  <div className="font-medium">
                    {formData.price_range_min && formData.price_range_max 
                      ? `$${formData.price_range_min} - $${formData.price_range_max}`
                      : '-'}
                  </div>
                  {formData.special_promotion && (
                    <>
                      <div className="text-gray-500">Promotion:</div>
                      <div className="font-medium text-gold-700">Special Promotion ($99/3 months)</div>
                    </>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-500">
                By submitting, you agree that your information will be reviewed by our team. 
                We'll notify you on this platform once your listing is approved.
              </p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <Link to="/">
                <Button variant="ghost" className="rounded-xl text-gray-500">
                  Cancel
                </Button>
              </Link>
            )}

            {step < 3 ? (
              <Button onClick={handleNext} className="btn-primary" data-testid="vendor-next-btn">
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                className="btn-primary"
                disabled={loading}
                data-testid="vendor-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BecomeVendorPage;
